import { useEffect, useState, useRef, Fragment } from "react";
import {
  Autocomplete,
  CircularProgress,
  DatePicker,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  Button,
  CurrencyInput,
  DataGrid,
} from "../../../components";
import { AddLayout } from "../../../layouts";
import {
  EventosFiscaisService,
  NfSaidaService,
  notification,
  NfEntradaService,
} from "../../../services";
import { InputErros, PermisoesHelper, value } from "../../../helpers";
import {
  responseErros,
  timeoutBuscaAutocomplete,
  userLogs,
  voltar,
  formatDate,
  formatPrice,
  unformatPrice,
} from "../../../utils";
import { tiposEvento } from "./constants";
import { EventoFiscal } from "./entities";
import {
  eventoFiscalValidator,
  itemEventoFiscalValidator,
} from "./middlewares";
import { ErrosEmitirDialog, ButtonMenu } from "./components";

const eventosFiscaisService = new EventosFiscaisService();
const nfSaidaService = new NfSaidaService();
const permissoesHelper = new PermisoesHelper();
const nfEntradaService = new NfEntradaService();

const AddEventoFicalView = ({ match }) => {
  const { id } = match.params;
  permissoesHelper.validarPermisao("nf-saida-visualizar");
  const [eventoFiscal, setEventoFiscal] = useState({});
  const [tipoEvento, setTipoEvento] = useState(null);
  const [listaNfSaida, setListaNfSaida] = useState([]);
  const [nfSaida, setNfSaida] = useState(null);
  const [listaNfEntrada, setListaNfEntrada] = useState([]);
  const [nfEntrada, setNfEntrada] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAutoCompleteNfSaida, setLoadingAutoCompleteNfSaida] =
    useState(false);
  const [loadingAutoCompleteNfEntrada, setLoadingAutoCompleteNfEntrada] =
    useState(false);
  const [inputErro, setInputErro] = useState([]);
  const [userLog, setUserLog] = useState({});
  const [errosRetorno, setErrosRetorno] = useState(null);
  const [errosRetornoDialogOpen, setErrosRetornoDialogOpen] = useState(false);
  const [listaProdutosNf, setListaProdutosNf] = useState([]);
  const [itensEventoFiscal, setItensEventoFiscal] = useState([]);
  const [produto, setProduto] = useState(null);
  const inputNfSaida = useRef();
  const inputErros = new InputErros(inputErro, setInputErro);
  const inputNfEntrada = useRef();

  const itemEventoFiscalColunas = [
    {
      field: "ordem",
      headerName: "Ordem",
      headerAlign: "center",
      align: "center",
      flex: 200,
    },
    {
      field: "unidade",
      headerName: "Unidade",
      headerAlign: "center",
      align: "center",
      flex: 200,
    },
    {
      field: "quantidade",
      headerName: "Qtd",
      headerAlign: "center",
      align: "center",
      flex: 200,
    },
    {
      field: "valorIbs",
      headerName: "Valor IBS",
      headerAlign: "center",
      align: "center",
      flex: 250,
      renderCell: (params) => formatPrice(params.value || 0),
    },
    {
      field: "valorCbs",
      headerName: "Valor CBS",
      headerAlign: "center",
      align: "center",
      flex: 250,
      renderCell: (params) => formatPrice(params.value || 0),
    },
  ];

  useEffect(() => {
    if (id) {
      buscarEventoFiscal();
    }
  }, [id]);

  async function buscarEventoFiscal() {
    setLoading(true);
    const result = await eventosFiscaisService.getById(id);
    setLoading(false);
    if (!result.isAxiosError) {
      setEventoFiscal(result.data);
      if (result.data.itens) {
        setItensEventoFiscal(result.data.itens);
      }
      buscarTipoEventoPorCodigo(result.data.codigoEvento);
      if (result.data.fatNfSaidaId) {
        buscarNfSaidaPorIdEdicao(result.data.fatNfSaidaId);
      }
      if (result.data.estNfEntradaId) {
        buscarNfEntradaPorIdEdicao(result.data.estNfEntradaId);
      }
    } else {
      responseErros(result);
    }
  }

  function buscarTipoEventoPorCodigo(codigoEvento) {
    const tipoEventoSelecionado = tiposEvento.find(
      (tipo) => tipo.codigo === codigoEvento,
    );
    setTipoEvento(tipoEventoSelecionado);
  }

  function buscarNfSaidaPorIdEdicao(nfSaidaId) {
    if (nfSaidaId) {
      nfSaidaService.getById(nfSaidaId).then((result) => {
        if (!result.isAxiosError) {
          setNfSaida(result.data);
          setListaProdutosNf(result.data?.nfSaidaItens || []);
        }
      });
    }
  }

  function buscarNfEntradaPorIdEdicao(nfEntradaId) {
    if (nfEntradaId) {
      nfEntradaService.getById(nfEntradaId).then((result) => {
        if (!result.isAxiosError) {
          setNfEntrada(result.data);
          setListaProdutosNf(result.data?.nfEntradaItens || []);
        }
      });
    }
  }

  const handleInputChange = (e) => {
    e.persist();
    setInputErro([]);
    setEventoFiscal((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const onChangeTipoEventoChange = (e) => {
    setInputErro([]);
    const tipoEvento = e.target.value ?? null;
    setTipoEvento(tipoEvento);
    setNfEntrada(null);
    setNfSaida(null);
    setListaProdutosNf([]);
    setItensEventoFiscal([]);
    setProduto(null);
    if (tipoEvento) {
      setEventoFiscal({
        descricaoEvento: tipoEvento.descricao,
        codigoEvento: tipoEvento.codigo,
      });
    } else {
      setEventoFiscal({});
    }
  };

  const onChangeAutocompleteNfSaida = (value) => {
    setNfSaida(value);
    setInputErro([]);
    setEventoFiscal((prevState) => ({
      ...prevState,
      fatNfSaidaId: value?.id ?? null,
      chaveAcesso: value?.chaveAcesso ?? null,
    }));
  };

  const onChangeAutocompleteNfEntrada = (value) => {
    setNfEntrada(value);
    setInputErro([]);
    setEventoFiscal((prevState) => ({
      ...prevState,
      estNfEntradaId: value?.id ?? null,
      chaveAcesso: value?.nfeChave ?? null,
    }));
    montarItensNfEntrada(value?.nfEntradaItens);
    setItensEventoFiscal([]);
  };

  function montarItensNfEntrada(itensNfEntrada) {
    if (itensNfEntrada && itensNfEntrada.length) {
      setListaProdutosNf(
        itensNfEntrada.map((item) => {
          return {
            id: item.id,
            ordem: item.ordem,
            unidade: item.unidade,
            quantidade: item.quantidade,
            valorIbs: item.tributosIbsAliquotaMun + item.tributosIbsAliquotaUf,
            valorCbs: item.tributosIbsAliquotaCbs,
            descricao: item.produto,
            preco: item.precoCompra,
          };
        }),
      );
      return;
    }
    setListaProdutosNf([]);
  }

  const buscarNfEntrada = async (filtro) => {
    setLoadingAutoCompleteNfEntrada(true);
    const result = await nfEntradaService.getAllFiltroAvancado(filtro);
    setLoadingAutoCompleteNfEntrada(false);
    if (!result.isAxiosError) {
      setListaNfEntrada(result.data.rows);
    } else {
      responseErros(result);
    }
  };

  const onSearchChangeNfEntrada = (event, inputRef) => {
    if (event.key === "Enter" && inputRef.current === document.activeElement) {
      timeoutBuscaAutocomplete(
        buscarNfEntrada({ numeroNota: event.target?.value }),
      );
    }
  };

  const buscarNfSaida = async (filtro) => {
    setLoadingAutoCompleteNfSaida(true);
    const result = await nfSaidaService.getAllFiltroAvancado(filtro);
    setLoadingAutoCompleteNfSaida(false);
    if (!result.isAxiosError) {
      setListaNfSaida(result.data.rows);
    } else {
      responseErros(result);
    }
  };

  const onSearchChangeNfSaida = (event, inputRef) => {
    if (event.key === "Enter" && inputRef.current === document.activeElement) {
      timeoutBuscaAutocomplete(
        buscarNfSaida({
          numeroDocumento: event.target?.value,
          status: "ENVIADA",
          restritiva: true,
        }),
      );
    }
  };

  async function onClickLog() {
    if ("userCreatedLog" in userLog) return;
    const logs = await userLogs(eventoFiscal);
    setUserLog(logs);
  }

  const desabilitarCampos = (campos, campo) => {
    if (id) return true;
    if (!campos) return true;
    return !campos.includes(campo);
  };

  const getVariant = (campos, campo) => {
    const desabilitado = desabilitarCampos(campos, campo);
    return desabilitado ? "filled" : "outlined";
  };

  const handleSubmit = async () => {
    eventoFiscalValidator
      .validate(eventoFiscal, { abortEarly: false })
      .then(async () => {
        setLoading(true);
        const dadosEvento = new EventoFiscal({
          ...eventoFiscal,
          itens: itensEventoFiscal,
        });
        const response = await eventosFiscaisService.cadastrar(dadosEvento);
        setLoading(false);
        if (!response.isAxiosError) {
          notification.cadastroSucesso();
          voltar();
        } else {
          if (response.status === 400) {
            setErrosRetorno(response?.response?.data?.message);
            setErrosRetornoDialogOpen(true);
            return;
          }
          responseErros(response);
        }
      })
      .catch((err) => {
        inputErros.set(err);
      });
  };

  const onChangeDate = (name, date) => {
    if (!date) {
      date = "Invalid Date";
    }
    setEventoFiscal({
      ...eventoFiscal,
      [name]: date != "Invalid Date" ? formatDate.toSend(date) : "",
    });
  };

  const handleChangeAutocompleteProduto = (produtoEscolhido) => {
    if (produtoEscolhido) {
      setProduto(produtoEscolhido);
    } else {
      setProduto(null);
    }
  };

  const handleInputChangeProduto = (e) => {
    const { name, value } = e.target;
    setProduto((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAdicionarProduto = () => {
    if (!produto) return;
    const produtoFormatado = {
      ...produto,
      id: produto.id || Date.now().toString(),
      ordem: Number(produto.ordem),
      quantidade: Number(produto.quantidade),
      valorIbs: Number(produto.valorIbs),
      valorCbs: Number(produto.valorCbs),
    };

    itemEventoFiscalValidator
      .validate(produtoFormatado, { abortEarly: false })
      .then(() => {
        setItensEventoFiscal((prev) => [...prev, produtoFormatado]);
        setProduto(null);
      })
      .catch((err) => {
        inputErros.set(err);
      });
  };

  const handleInputBlur = (event) => {
    const { name, value: val } = event.target;
    const valorInserido = parseFloat(unformatPrice(val)) || 0;
    setProduto((prev) => ({
      ...prev,
      [name]: valorInserido,
    }));
  };

  const removerItem = (idItem) => {
    setItensEventoFiscal((prev) => prev.filter((item) => item.id !== idItem));
  };

  const onClickCancelar = async () => {
    const retornoAlerta = await notification.confirmacao(
      "Cancelar!",
      "Tem certeza que deseja cancelar esse evento fiscal?",
    );
    if (retornoAlerta.isConfirmed) {
      setLoading(true);
      const response = await eventosFiscaisService.cancelar(id);
      setLoading(false);
      if (!response.isAxiosError) {
        notification.alteracaoSucesso();
        voltar();
      } else {
        responseErros(response);
      }
    }
  };

  return (
    <AddLayout
      id={id}
      title={id ? "Editar Evento Fiscal" : "Cadastrar Evento Fiscal"}
      onClickSalvar={handleSubmit}
      loading={loading}
      userLog={id ? userLog : null}
      onClickLog={onClickLog}
      showUpdate={false}
      ButtonMenu={
        id && eventoFiscal?.status == "ENVIADA" ? (
          <ButtonMenu onClickCancelar={onClickCancelar} />
        ) : null
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Select
            id="codigoEvento"
            name="codigoEvento"
            label="Tipo de evento"
            value={tipoEvento ?? ""}
            onChange={onChangeTipoEventoChange}
            error={inputErros.get("codigoEvento")}
            required
            variant={id ? "filled" : "outlined"}
            disabled={id}
          >
            <MenuItem value="">
              <em></em>
            </MenuItem>
            {tiposEvento.map((tipo) => (
              <MenuItem key={tipo.codigo} value={tipo}>
                {tipo.codigo} - {tipo.descricao}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={4}>
          <DatePicker
            id="dataPrevistaEntrega"
            name="dataPrevistaEntrega"
            label="Data Prevista de Entrega"
            format="dd/MM/yyyy"
            value={value.date(eventoFiscal.dataPrevistaEntrega)}
            onChange={(date) => onChangeDate("dataPrevistaEntrega", date)}
            error={inputErros.get("dataPrevistaEntrega")}
            disabled={desabilitarCampos(
              tipoEvento?.campos,
              "dataPrevistaEntrega",
            )}
            variant={getVariant(tipoEvento?.campos, "dataPrevistaEntrega")}
          />
        </Grid>
        <Grid item xs={2}>
          <Select
            id="aceiteCreditoDebitoNota"
            name="aceiteCreditoDebitoNota"
            label="Aceite de Crédito/Débito"
            value={eventoFiscal.aceiteCreditoDebitoNota ?? ""}
            onChange={(e) =>
              setEventoFiscal({
                ...eventoFiscal,
                aceiteCreditoDebitoNota: e.target.value,
              })
            }
            error={inputErros.get("aceiteCreditoDebitoNota")}
            required
            variant={getVariant(tipoEvento?.campos, "aceiteCreditoDebitoNota")}
            disabled={desabilitarCampos(
              tipoEvento?.campos,
              "aceiteCreditoDebitoNota",
            )}
          >
            <MenuItem value={"Aceite"}>Sim</MenuItem>
            <MenuItem value={"NaoAceite"}>Não</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={3}>
          <FormControl variant="outlined" fullWidth>
            <Autocomplete
              id="fatNfSaidaId"
              name="fatNfSaidaId"
              options={listaNfSaida}
              autoHighlight
              disabled={desabilitarCampos(tipoEvento?.campos, "fatNfSaidaId")}
              loading={loadingAutoCompleteNfSaida}
              loadingText="Carregando"
              noOptionsText={
                document.getElementById("fatNfSaidaId")?.value.length >= 1 ? (
                  <Grid container spacing={1}>
                    <Grid item xs={12} fullWidth>
                      Nenhum NF de saida encontrado
                    </Grid>
                  </Grid>
                ) : (
                  "Digite e pressione Enter"
                )
              }
              onChange={(e, value) => onChangeAutocompleteNfSaida(value)}
              getOptionLabel={(option) =>
                `Numero Documento: ${option?.numeroDocumento}`
              }
              renderOption={(props, option) => (
                <li {...props} style={{ background: "transparent" }}>
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <span className="fw-bold">{`Numero Documento: ${option?.numeroDocumento}`}</span>
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="NF de Saída"
                  variant={getVariant(tipoEvento?.campos, "fatNfSaidaId")}
                  error={inputErros.get("fatNfSaidaId")}
                  onKeyDown={(e) => onSearchChangeNfSaida(e, inputNfSaida)}
                  required
                  inputRef={inputNfSaida}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {loadingAutoCompleteNfSaida ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                />
              )}
              value={nfSaida}
            />
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl variant="outlined" fullWidth>
            <Autocomplete
              id="estNfEntradaId"
              name="estNfEntradaId"
              options={listaNfEntrada}
              autoHighlight
              disabled={desabilitarCampos(tipoEvento?.campos, "estNfEntradaId")}
              loading={loadingAutoCompleteNfEntrada}
              loadingText="Carregando"
              noOptionsText={
                document.getElementById("estNfEntradaId")?.value.length >= 1 ? (
                  <Grid container spacing={1}>
                    <Grid item xs={12} fullWidth>
                      Nenhum NF de entrada encontrado
                    </Grid>
                  </Grid>
                ) : (
                  "Digite e pressione Enter"
                )
              }
              onChange={(e, value) => onChangeAutocompleteNfEntrada(value)}
              getOptionLabel={(option) =>
                `Numero Documento: ${option?.numeroNota}`
              }
              renderOption={(props, option) => (
                <li {...props} style={{ background: "transparent" }}>
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <span className="fw-bold">{`Numero Documento: ${option?.numeroNota}`}</span>
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="NF de Entrada"
                  variant={getVariant(tipoEvento?.campos, "estNfEntradaId")}
                  error={inputErros.get("estNfEntradaId")}
                  onKeyDown={(e) => onSearchChangeNfEntrada(e, inputNfEntrada)}
                  required
                  inputRef={inputNfEntrada}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {loadingAutoCompleteNfEntrada ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                />
              )}
              value={nfEntrada}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="chaveAcesso"
            name="chaveAcesso"
            label="Chave de Acesso"
            disabled={desabilitarCampos(tipoEvento?.campos, "chaveAcesso")}
            variant={getVariant(tipoEvento?.campos, "chaveAcesso")}
            error={inputErros.get("chaveAcesso")}
            value={value.text(eventoFiscal.chaveAcesso)}
            onChange={(e) => handleInputChange(e)}
            required
          />
        </Grid>
      </Grid>
      <hr className="mt-4 mb-2" />
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="produtoId"
              name="produtoId"
              disabled={desabilitarCampos(tipoEvento?.campos, "itens")}
              options={listaProdutosNf}
              renderOption={(props, option) => {
                return option ? (
                  <li {...props} style={{ background: "transparent" }}>
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <div>
                        <span className="fw-bold">{`${option?.ordem} - ${option?.descricao}`}</span>
                      </div>
                      <div className="text-end">
                        <Typography variant="body2">Preço</Typography>
                        <span className="fs-6">
                          {formatPrice(option?.preco || 0)}
                        </span>
                      </div>
                    </div>
                  </li>
                ) : (
                  ""
                );
              }}
              getOptionLabel={(option) =>
                option?.codigo ? `${option?.codigo} - ${option?.descricao}` : ""
              }
              onChange={(_, newValue) =>
                handleChangeAutocompleteProduto(newValue)
              }
              value={produto}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant={getVariant(tipoEvento?.campos, "itens")}
                  label="Selecionar item"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      paddingRight: "12px !important",
                    },
                  }}
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={1}>
          <TextField
            id="ordem"
            name="ordem"
            label="Ordem"
            variant={getVariant(tipoEvento?.campos, "itens")}
            type="number"
            required
            margin="normal"
            error={inputErros.get("ordem")}
            disabled={desabilitarCampos(tipoEvento?.campos, "itens")}
            InputLabelProps={{ shrink: true }}
            value={produto?.ordem ? produto.ordem : ""}
            onChange={handleInputChangeProduto}
          />
        </Grid>
        <Grid item xs={1}>
          <TextField
            id="unidade"
            name="unidade"
            label="Unidade"
            variant={getVariant(tipoEvento?.campos, "itens")}
            margin="normal"
            error={inputErros.get("unidade")}
            required
            disabled={desabilitarCampos(tipoEvento?.campos, "itens")}
            InputLabelProps={{ shrink: true }}
            value={produto?.unidade ? produto.unidade : ""}
            onChange={handleInputChangeProduto}
            fullWidth
          />
        </Grid>
        <Grid item xs={1}>
          <TextField
            id="quantidade"
            name="quantidade"
            label="Qtd"
            error={inputErros.get("quantidade")}
            variant={getVariant(tipoEvento?.campos, "itens")}
            margin="normal"
            disabled={desabilitarCampos(tipoEvento?.campos, "itens")}
            type="number"
            value={value.text(produto?.quantidade)}
            onChange={handleInputChangeProduto}
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={2}>
          <CurrencyInput
            id="valorIbs"
            name="valorIbs"
            label="Valor Ibs R$"
            error={inputErros.get("valorIbs")}
            required
            disabled={desabilitarCampos(tipoEvento?.campos, "itens")}
            value={produto?.valorIbs}
            variant={getVariant(tipoEvento?.campos, "itens")}
            onBlur={handleInputBlur}
          />
        </Grid>
        <Grid item xs={2}>
          <CurrencyInput
            id="valorCbs"
            name="valorCbs"
            label="Valor Cbs R$"
            error={inputErros.get("valorCbs")}
            required
            disabled={desabilitarCampos(tipoEvento?.campos, "itens")}
            value={produto?.valorCbs}
            variant={getVariant(tipoEvento?.campos, "itens")}
            onBlur={handleInputBlur}
          />
        </Grid>
        <Grid item xs={1} className="mt-4">
          <Button
            id="buttonAdicionar"
            variant="contained"
            color="success"
            disabled={desabilitarCampos(tipoEvento?.campos, "itens")}
            size="small"
            sx={{
              height: "35px",
              borderRadius: "50%",
              minWidth: "15px",
              boxShadow: "none",
            }}
            onClick={() => handleAdicionarProduto()}
          >
            <i
              className="ph-bold ph-plus"
              style={{ color: "#fff", fontSize: 15 }}
            ></i>
          </Button>
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            style={{ zIndex: 1 }}
            rows={itensEventoFiscal}
            columns={itemEventoFiscalColunas}
            rowCount={itensEventoFiscal.length}
            onClickDelete={(id) => removerItem(id)}
            deleteShow={!id}
          />
        </Grid>
      </Grid>
      <ErrosEmitirDialog
        open={errosRetornoDialogOpen}
        setOpen={setErrosRetornoDialogOpen}
        errosEmitir={errosRetorno}
      />
    </AddLayout>
  );
};

export default AddEventoFicalView;
